import React from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveMediaUrl } from '../../utils/api';
import { createWhatsAppLink } from '../../utils/formatters';
import { LetGoCardsGallery } from './LetGoCardsGallery';
import { motion } from 'motion/react';
import { Truck, MapPin, MessageCircle, Sparkles, Coffee, Clock } from 'lucide-react';

export const MobileTruckSection: React.FC = () => {
  const { data } = useContent();
  const { mobileService, contactSettings } = data;

  const photo = resolveMediaUrl(mobileService.bgImage || mobileService.truckImage);

  const locationsList =
    mobileService.locations && mobileService.locations.length > 0
      ? mobileService.locations
      : ['Parkiran MPP', 'Ecopark'];

  const whatsappNumber = contactSettings?.whatsapp || '082168936647';
  const askScheduleWaLink = createWhatsAppLink(
    whatsappNumber,
    "Halo LetonKu, saya ingin menanyakan lokasi mangkal & jadwal mobile coffee LET'GO hari ini."
  );

  return (
    <section
      id="let-go"
      className="relative w-full overflow-hidden py-8 sm:py-14 px-4 sm:px-6 lg:px-8 bg-[#F8F5EF] border-t border-[#E8E0D6] font-body"
    >
      <div className="max-w-7xl mx-auto w-full relative z-10">
        {/* Main Split Showcase Card */}
        <div className="bg-[#FFFFFF] rounded-3xl border border-[#E8E0D6] shadow-[0_8px_30px_rgba(43,29,23,0.06)] overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
          {/* Visual Column (5 cols) */}
          <div className="lg:col-span-5 relative min-h-[260px] sm:min-h-[360px] lg:min-h-full bg-[#F8F5EF]">
            {photo ? (
              <img
                src={photo}
                alt={mobileService.title || "LET'GO Mobile Coffee"}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (target.src !== '/assets/leton-coffee-beans.jpg' && !target.src.endsWith('/assets/leton-coffee-beans.jpg')) {
                    target.src = '/assets/leton-coffee-beans.jpg';
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[#39271F] p-8 text-center">
                <Truck className="w-16 h-16 opacity-35 mb-2" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[#756B64]">
                  LET'GO Mobile Unit
                </span>
              </div>
            )}

            {/* Active Status Badge */}
            <div className="absolute top-4 left-4">
              <span className="px-3.5 py-1.5 rounded-full bg-[#FFFFFF]/95 backdrop-blur-md text-[#39271F] text-xs font-semibold uppercase tracking-wider shadow-xs flex items-center gap-1.5 border border-[#E8E0D6]">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block animate-pulse" />
                <span>Unit Mobile Aktif</span>
              </span>
            </div>
          </div>

          {/* Details Column (7 cols) */}
          <div className="lg:col-span-7 p-6 sm:p-10 lg:p-12 flex flex-col justify-between">
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#B77945]/12 text-[#B77945] border border-[#B77945]/20 text-xs font-semibold uppercase tracking-wider mb-4">
                <Truck className="w-3.5 h-3.5 text-[#B77945]" />
                <span>04 — {mobileService.badge || 'MOBILE COFFEE EXPERIENCE'}</span>
              </div>

              {/* Title */}
              <h2 className="font-heading font-bold text-2xl sm:text-4xl text-[#2B1D17] tracking-[-0.02em] leading-tight">
                {mobileService.title || "LET'GO"}
              </h2>

              {/* Subtitle / Tagline */}
              <p className="mt-2 text-xs sm:text-sm font-semibold text-[#B77945] uppercase tracking-wider">
                {mobileService.subtitle || 'COFFEE ON THE MOVE'}
              </p>

              {/* Description */}
              <p className="mt-3.5 text-xs sm:text-sm text-[#756B64] leading-relaxed">
                {mobileService.description ||
                  'LetonKu hadir lebih dekat dengan kamu melalui konsep mobile coffee booth. Temukan kami di lokasi-lokasi tertentu dan nikmati kopi racikan barista Leton tanpa harus datang ke outlet utama.'}
              </p>

              {/* Mobile Locations Grid */}
              <div className="mt-6 pt-5 border-t border-[#E8E0D6]">
                <span className="text-xs font-semibold tracking-wider uppercase text-[#756B64] flex items-center gap-1.5 mb-3">
                  <MapPin className="w-3.5 h-3.5 text-[#B77945]" />
                  <span>Titik Lokasi Mangkal:</span>
                </span>

                <div className="flex flex-wrap items-center gap-2.5">
                  {locationsList.map((loc, idx) => (
                    <div
                      key={idx}
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#F8F5EF] border border-[#E8E0D6] text-[#2B1D17] text-xs sm:text-sm font-semibold tracking-wide shadow-2xs"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#B77945]" />
                      <span>{loc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Highlights Micro Badges */}
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-[#F8F5EF] border border-[#E8E0D6] flex items-center gap-2.5">
                  <Coffee className="w-4 h-4 text-[#B77945] shrink-0" />
                  <span className="text-xs text-[#2B1D17] font-semibold">Racikan Espresso &amp; Minuman Segar</span>
                </div>
                <div className="p-3 rounded-xl bg-[#F8F5EF] border border-[#E8E0D6] flex items-center gap-2.5">
                  <Clock className="w-4 h-4 text-[#B77945] shrink-0" />
                  <span className="text-xs text-[#2B1D17] font-semibold">Pelayanan Cepat &amp; Praktis</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-7 pt-5 border-t border-[#E8E0D6] flex flex-wrap items-center gap-3">
              <a
                href={askScheduleWaLink}
                target="_blank"
                rel="noopener noreferrer"
                id="letgo-whatsapp-btn"
                className="px-6 py-3 rounded-xl bg-[#39271F] hover:bg-[#2B1D17] text-white font-semibold text-xs sm:text-sm tracking-wide flex items-center gap-2 shadow-[0_6px_16px_rgba(57,39,31,0.18)] transition-all cursor-pointer"
              >
                <MessageCircle className="w-4 h-4 text-[#B77945]" />
                <span>Tanya Jadwal / Cek Lokasi Hari Ini</span>
              </a>
            </div>
          </div>
        </div>

        {/* Horizontal Photo Slider / Carousel for LET'GO */}
        <LetGoCardsGallery
          images={mobileService.letGoGalleryImages || []}
          sectionLabel="LET'GO"
        />
      </div>
    </section>
  );
};
